<script setup lang="ts">
import type { File } from '~/types';
import { TransitionRoot } from '@headlessui/vue';
import { computed, ref } from 'vue';

interface GalleryFile extends File {
  categories?: Array<{ categories_id?: { title?: string; color?: string; headline?: string } }>;
}

export interface GalleryProps {
  items: GalleryFile[];
  group_by_category?: boolean;
}

const props = defineProps<GalleryProps>();

const {
  isOpen,
  currentItemIdx,
  isHelpOpen,
  modalImageIndexes,
  currentItem,
  items: modalItems,
  openGallery,
  next,
  prev,
  close,
  toggleHelp,
} = useGalleryModal();

const currentCategoryKey = ref<string | null>(null); // Track the selected category when grouping

const { group_by_category = false } = props; // default to false if not provided

// Group by category
const groupedByCategory = computed(() => {
  const categoryMap: Record<
    string,
    {
      color?: string;
      headline?: string;
      title?: string;
      items: Array<File>;
    }
  > = {};

  for (const item of props.items ?? []) {
    const categories = (item as GalleryFile).categories ?? [];

    for (const categoryWrapper of categories) {
      const category = categoryWrapper.categories_id;
      if (!category || !category.title) continue;

      const key = category.title;

      if (!categoryMap[key]) {
        categoryMap[key] = {
          color: category.color,
          headline: category.headline,
          title: category.title,
          items: [],
        };
      }

      categoryMap[key].items.push(item);
    }
  }

  return categoryMap;
});

function getPrimaryCategoryKey(item: GalleryFile) {
  return item.categories?.find((categoryWrapper) => categoryWrapper.categories_id?.title)?.categories_id?.title;
}

function openGalleryItem(item: GalleryFile, index: number) {
  if (!group_by_category) {
    openGallery(props.items, index);
    return;
  }

  const categoryKey = getPrimaryCategoryKey(item);
  currentCategoryKey.value = categoryKey ?? null;

  const categoryGroup = categoryKey ? groupedByCategory.value[categoryKey] : undefined;

  if (!categoryGroup) {
    openGallery(props.items, index);
    return;
  }

  const categoryIndex = categoryGroup.items.findIndex((categoryItem) => categoryItem.id === item.id);
  openGallery(categoryGroup.items, categoryIndex >= 0 ? categoryIndex : 0);
}

</script>

<template>
  <!-- Debugging -->
  <!-- <pre>{{ groupedByCategory }}</pre>
  {{ group_by_category }} -->

  <!-- Show every gallery item. When grouping is enabled, the modal opens scoped to the clicked item's category. -->
  <div class="gap-4 mt-4 md:columns-3">
    <button
      v-for="(item, itemIdx) in items"
      :key="item.id"
      class="block relative w-full mb-6 overflow-hidden border cursor-pointer dark:border-gray-700 rounded-card focus:outline-none"
      @click="openGalleryItem(item, itemIdx)"
    >
      <div class="relative block w-full overflow-hidden rounded-card group">
        <NuxtImg
          :src="item.id"
          :alt="item.description ?? ''"
          preload
          height="360"
          class="object-cover w-full transition duration-300 group-hover:scale-110"
          placeholder-class="bg-grey-500 animate-pulse"
        />
        <div
          class="absolute inset-0 flex items-center justify-center transition-opacity duration-300 bg-white/75 opacity-0 cursor-pointer hover:opacity-100 dark:bg-gray-900/75"
        >
          <UIcon name="material-symbols:zoom-in-rounded" class="w-12 h-12 text-primary" />
        </div>
      </div>
    </button>
  </div>

  <!-- Gallery Modal -->
  <TransitionRoot
    :show="isOpen"
    enter="ease-out duration-300"
    enter-from="opacity-0"
    enter-to="opacity-100"
    leave="ease-in duration-200"
    leave-from="opacity-100"
    leave-to="opacity-0"
  >
    <div class="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-gray-900/75">
      <div class="relative flex flex-col items-center justify-center w-full h-full max-w-7xl text-white">
        <!-- Help Button -->
        <div
          v-auto-animate
          class="absolute z-50 hidden gap-2  bg-opacity-75 item-center md:flex top-4 left-4"
        >
          <div>
            <UButton
              :icon="isHelpOpen ? 'material-symbols:hide' : 'material-symbols:help-outline'"
              class="text-white"
              size="xl"
              @click="toggleHelp"
            />
          </div>

          <div
            v-if="isHelpOpen"
            class="flex items-center px-3 py-1 text-xs bg-gray-900 backdrop-blur-sm rounded-button gap-x-4"
          >
            <p>
              Press
              <UKbd value="esc" class="dark:text-white text-black"/>
              to close
            </p>
            <p>
              Press
              <UKbd value="←" class="dark:text-white text-black"/>
              or
              <UKbd value="→" class="dark:text-white text-black"/>
              to navigate
            </p>
          </div>
        </div>
        <!-- Close Button -->
        <!--button to direct user to full gallery page with passed group by type but only if group_by_category is true-->
        <UButton
          v-if="group_by_category && currentCategoryKey"
          icon="material-symbols:open-in-new"
          size="xl"
          trailing
          class="absolute z-50 top-4 text-white"
          @click="() => { $router.push({ name: 'projects', query: { group_by_category: currentCategoryKey } }) }"
        >
          See More Of Our {{ currentCategoryKey }} Projects
        </UButton>
        <UButton class="absolute z-50 top-4 right-4 text-white" icon="material-symbols:close-rounded" size="xl" @click="close" />

        <div class="flex items-center justify-center w-full h-full">
          <!-- Nav Buttons -->
          <UButton class="absolute z-50 left-4 text-white" icon="material-symbols:arrow-back-rounded" size="xl" @click="prev" />
          <UButton
            class="absolute z-50 right-4 text-white"
            icon="material-symbols:arrow-forward-rounded"
            size="xl"
            @click="next"
          />

          <!-- Image Area -->
          <div class="relative flex flex-col items-center justify-center w-full h-full p-20">
            <p
              v-if="currentItem?.description"
              class="inline-block px-6 py-2 text-sm text-white bg-gray-900 rounded-t-xl"
            >
              {{ currentItem.description }}
            </p>

            <template v-for="(item, idx) in modalItems" :key="item.id">
              <NuxtImg
                v-if="modalImageIndexes.includes(idx)"
                v-show="currentItemIdx === idx"
                :src="item.id"
                :alt="item.description ?? ''"
                :preload="currentItemIdx === idx"
                :loading="currentItemIdx === idx ? 'eager' : 'lazy'"
                height="800"
                class="object-contain w-full rounded-card"
                placeholder-class="bg-grey-500 animate-pulse"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </TransitionRoot>
</template>
